import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SamFilter, SamFilterState, SamFilterType } from "../filterTypes";
import { AddFilterAction, RemoveFiltersAction, SetFiltersAction, SetSearchFiltersAction, withFilterState } from "../utils/withFilterState";
import {
  InvertPartlistEntriesAction,
  PartlistFilter,
  PartlistFilterableField,
  PartlistFilterQueryParams,
  PartlistFilterState,
  PartlistSelection,
} from "./partlistTypes";

export const samPartlistSliceName = "samPartlist";

export interface SamPartlistState {
  filters: PartlistFilterState;
  filterQueryParams?: PartlistFilterQueryParams;
  filterTypes: SamFilterType[];
  selection: PartlistSelection;
  selectionRequest?: string[];
}

const initialState: SamPartlistState = {
  filters: {},
  filterTypes: [],
  selection: {
    partlistEntryIds: [],
    additionalKeys: [],
  },
};

const samSlice = createSlice({
  name: samPartlistSliceName,
  initialState,
  reducers: {
    setPartlistSelection(state: SamPartlistState, action: PayloadAction<PartlistSelection>) {
      state.selection = action.payload;
    },
    setPartlistFilterTypes(state: SamPartlistState, action: PayloadAction<SamFilterType[]>) {
      state.filterTypes = action.payload;
    },
    setPartlistFilters(state: SamPartlistState, action: PayloadAction<SetFiltersAction<PartlistFilter>>) {
      withFilterState(state.filters).setFilters(action.payload);
    },
    setPartlistSearchFilters(state: SamPartlistState, action: PayloadAction<SetSearchFiltersAction>) {
      withFilterState(state.filters).setSearchFilters(action.payload);
    },
    addPartlistExcludeFilter(state: SamPartlistState, action: PayloadAction<AddFilterAction<PartlistFilter>>) {
      withFilterState(state.filters).addExcludeFilter(action.payload);
    },
    addPartlistIncludeFilter(state: SamPartlistState, action: PayloadAction<AddFilterAction<PartlistFilter>>) {
      withFilterState(state.filters).addIncludeFilter(action.payload);
    },
    setPartlistFiltersForSource(state: SamPartlistState, action: PayloadAction<SetFiltersAction<PartlistFilter>>) {
      withFilterState(state.filters).setFiltersForSource(action.payload);
    },
    removePartlistFiltersForSource(state: SamPartlistState, action: PayloadAction<RemoveFiltersAction>) {
      withFilterState(state.filters).removeFiltersForSource(action.payload);
    },
    invertPartlistFilters(state: SamPartlistState, action: PayloadAction<SamFilterType>) {
      withFilterState(state.filters).invertFilters(action.payload);
    },
    invertPartlistEntriesFilters(state: SamPartlistState, action: PayloadAction<InvertPartlistEntriesAction>) {
      invert(state.filters, action.payload);
    },
    clearPartlistFilters(state: SamPartlistState, action: PayloadAction<SamFilterType>) {
      withFilterState(state.filters).clearFilters(action.payload);
    },
    clearAllPartlistFilters(state: SamPartlistState) {
      withFilterState(state.filters).clearAllFilters();
    },
    setPartlistFilterQueryParams(state: SamPartlistState, action: PayloadAction<PartlistFilterQueryParams>) {
      state.filterQueryParams = action.payload;
    },
    setPartlistSelectionRequest(state: SamPartlistState, action: PayloadAction<string[] | undefined>) {
      state.selectionRequest = action.payload;
    },
  },
});

function invert<T extends SamFilter>(filterState: SamFilterState<T>, { partlistEntryIds, type }: InvertPartlistEntriesAction) {
  const partlistFilterElement = filterState[type];

  let workEntryIds = partlistEntryIds;
  const operators = ["==", "!=", "in", "!in"];

  const excludeIdMaps = new Map<string, string[]>();
  const includeIdMaps = new Map<string, string[]>();
  operators.forEach((operator) => {
    excludeIdMaps.set(operator, []);
    includeIdMaps.set(operator, []);
  });

  if (partlistFilterElement) {
    for (let index = partlistFilterElement.length - 1; index >= 0; index--) {
      const filter = partlistFilterElement[index];

      if (filter.field === PartlistFilterableField.PARTLIST_ENTRY_ID.toString()) {
        const newWorkEntryIds: string[] = [];
        workEntryIds.forEach((entryId) => {
          if (filter.value === entryId) {
            console.log("One entryId set!");
          } else if (filter.value === entryId || (typeof filter.value === "object" && (filter.value as string[]).includes(entryId))) {
            if (filter.action === "INCLUDE") excludeIdMaps.get(filter.operator)?.push(entryId);
            else if (filter.action === "EXCLUDE") includeIdMaps.get(filter.operator)?.push(entryId);
          } else newWorkEntryIds.push(entryId); // ready for next round
        });
        workEntryIds = newWorkEntryIds;
      }
    }
  }

  includeIdMaps.get("in")?.push(...workEntryIds);

  excludeIdMaps.forEach((excludeIds, operator) => {
    if (excludeIds.length > 0) {
      const excludeFilter = {
        field: "PARTLIST_ENTRY_ID",
        operator: operator,
        value: excludeIds,
      };
      const excludeSource = `PARTLIST:${excludeIds.join(",")}`;
      const filterAction: AddFilterAction<T> = { type, source: excludeSource, filter: excludeFilter as T };
      withFilterState(filterState).addExcludeFilter(filterAction);
    }
  });

  includeIdMaps.forEach((includeIds, operator) => {
    if (includeIds.length > 0) {
      const includeFilter = {
        field: "PARTLIST_ENTRY_ID",
        operator: operator,
        value: includeIds,
      };
      const includeSource = `PARTLIST:${includeIds.join(",")}`;
      const filterAction: AddFilterAction<T> = { type, source: includeSource, filter: includeFilter as T };
      withFilterState(filterState).addIncludeFilter(filterAction);
    }
  });
}

export interface SamPartlistRootState {
  [samPartlistSliceName]: SamPartlistState;
}

export const selectSamPartlist = (state: SamPartlistRootState) => state[samPartlistSliceName];
export const selectSamPartlistSelection = (state: SamPartlistRootState) => {
  return selectSamPartlist(state).selection;
};
export const selectSamPartlistFilters = (state: SamPartlistRootState) => {
  return selectSamPartlist(state).filters;
};
export const selectSamPartlistSelectionRequest = (state: SamPartlistRootState) => {
  return selectSamPartlist(state).selectionRequest;
};

export const selectPartlistFilterQueryParams = (state: SamPartlistRootState) => {
  return selectSamPartlist(state).filterQueryParams;
};

export const selectPartlistFilterTypes = (state: SamPartlistRootState) => {
  return selectSamPartlist(state).filterTypes;
};

export const {
  setPartlistSelection,
  setPartlistFilters,
  setPartlistSearchFilters,
  addPartlistExcludeFilter,
  addPartlistIncludeFilter,
  setPartlistFiltersForSource,
  removePartlistFiltersForSource,
  invertPartlistFilters,
  invertPartlistEntriesFilters,
  clearPartlistFilters,
  clearAllPartlistFilters,
  setPartlistSelectionRequest,
  setPartlistFilterQueryParams,
  setPartlistFilterTypes,
} = samSlice.actions;
export const samPartlistReducer = samSlice.reducer;
